// RolesDetailsGrid.tsx
import React, { useEffect, useMemo, useState } from 'react';
import './RolesDetailsGrid.css';
import BasicCli from '../../main/components/basicCli/BasicCli';
import eFormType from '../../main/assets/enums/eFormType';
import DeleteModal from '../../main/components/modal/delete/DeleteModal';
import { ApiConstants } from '../../main/constants/ApiConstants';
import IRole from '../../main/interfaces/IRole';
import RoleInventory from './roleInventory/RoleInventory';
import axiosInstance from '../../main/initializers/axios';
import EditModal from '../../main/components/modal/edit/EditModal';

import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import RoleGrantsGrid from './RoleGrantsGrid';
import { useTranslation } from 'react-i18next';
import useGetUser from '../../main/hooks/useGetUser';
import { useNavigate } from 'react-router-dom';

type CompanyOption = {
  id: number;
  name: string;
};

const RolesDetailsGrid: React.FC = () => {
  const currentUser = useGetUser();
  const navigate = useNavigate();
  const isSuperAdmin = (currentUser?.role?.code ?? '').toLowerCase() === 'superadmin';

  const [roles, setRoles] = useState<IRole[]>([]);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalEditOpen, setIsModalEditOpen] = useState(false);
  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);

  const [aprovedUser, setAprovedUser] = useState(false);
  const [selectedVal, setSelectedVal] = useState<IRole | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const { i18n } = useTranslation();

  const getTable = () => {
    axiosInstance.get(ApiConstants.Roles.list).then((res) => {
      setRoles(res.data ?? []);
    });
  };

  const getCompanies = () => {
    axiosInstance.get(ApiConstants.Company.list).then((res) => {
      setCompanies(res.data ?? []);
    });
  };

  useEffect(() => {
    getTable();
    getCompanies();
  }, []);

  useEffect(() => {
    if (!isSuperAdmin && currentUser?.companyId && selectedCompanyId === 0) {
      setSelectedCompanyId(currentUser.companyId);
    }
  }, [currentUser?.companyId, selectedCompanyId, isSuperAdmin]);

  const deleteDataSet = (id: number) => {
    axiosInstance.post(ApiConstants.Roles.delete, { Id: id }).then(() => {
      setIsModalDeleteOpen(false);
      setRoles((prev) => prev.filter((r: any) => r.id !== id));
    });
  };

  const filteredRoles = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let scoped = roles;
    if (selectedCompanyId > 0) {
      scoped = scoped.filter((r) => r.companyId === selectedCompanyId);
    }
    if (!q) return scoped;

    return scoped.filter((r) => {
      const id = (r.id ?? '').toString().toLowerCase();
      const name = (r.name ?? '').toLowerCase();
      const code = (r.code ?? '').toLowerCase();
      const company = (companies.find((c) => c.id === r.companyId)?.name ?? '').toLowerCase();
      return id.includes(q) || name.includes(q) || code.includes(q) || company.includes(q);
    });
  }, [roles, searchQuery, selectedCompanyId, companies]);

  return (
    <BasicCli>
      <div className="rdWrap">
        <div className="rdContainer">
          {/* Top bar */}
          <div className="ui-panel rdTopbar">
            <div className="rdSearch">
              <SearchIcon className="rdSearchIcon" />
              <input
                className="ui-control ui-input"
                placeholder={i18n.t('generic.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="rdActions">
              <select
                className="ui-control ui-input rdCompanySelect"
                value={selectedCompanyId}
                onChange={(e) => setSelectedCompanyId(Number(e.target.value) || 0)}
                disabled={!isSuperAdmin}
                title={isSuperAdmin ? 'Select company' : 'Your company'}
              >
                {isSuperAdmin && <option value={0}>All Companies</option>}
                {!isSuperAdmin && (
                  <option value={currentUser?.companyId ?? 0}>{currentUser?.companyName ?? 'Company'}</option>
                )}
                {companies.length === 0 && isSuperAdmin && <option value={selectedCompanyId || 0}>{currentUser?.companyName ?? 'Company'}</option>}
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>

              {isSuperAdmin && (
                <button className="ui-control ui-btn ui-btn--ghost" type="button" onClick={() => navigate('/role-actions')}>
                  Actions
                </button>
              )}

              <button className="ui-control ui-btn ui-btn--primary" type="button" onClick={() => setIsModalOpen(true)}>
                <AddIcon fontSize="small" />
                {i18n.t('roles.addRoles')}
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="bcCard rdTable">
            <table className="rdNativeTable">
              <thead>
                <tr>
                  <th style={{ width: 260 }}>{i18n.t('generic.name')}</th>
                  <th style={{ width: 180 }}>{i18n.t('generic.code')}</th>
                  <th style={{ width: 220 }}>{i18n.t('company.company', { defaultValue: 'Company' })}</th>
                  <th style={{ width: 160, textAlign: 'right' }}>{i18n.t('umb.actions')}</th>
                </tr>
              </thead>

              <tbody>
                {filteredRoles.map((item) => (
                  <tr key={item.id}>
                    <td className="rdStrong">{item.name}</td>
                    <td>{item.code}</td>
                    <td>
                      <span className="rdBadge">{companies.find((c) => c.id === item.companyId)?.name ?? `#${item.companyId}`}</span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="rdBtnGroup">
                        <button
                          type="button"
                          className="ui-control ui-btn ui-btn--ghost rdIconBtn"
                          title={i18n.t('generic.edit')}
                          disabled={(item.code ?? '').toLowerCase() === 'admin'}
                          onClick={() => {
                            setIsModalEditOpen(true);
                            setSelectedVal(item);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </button>

                        <button
                          type="button"
                          className="ui-control ui-btn ui-btn--ghost rdIconBtnDanger"
                          title={i18n.t('generic.delete')}
                          disabled={(item.code ?? '').toLowerCase() === 'admin'}
                          onClick={() => {
                            setIsModalDeleteOpen(true);
                            setSelectedVal(item);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {filteredRoles.length === 0 && (
                  <tr>
                    <td colSpan={4} className="rdEmpty">
                      {i18n.t('roles.noRolesFound')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Edit */}
          {isModalEditOpen && selectedVal && (
            <EditModal
              title={i18n.t('generic.edit')}
              width="90%"
              closeModal={() => setIsModalEditOpen(false)}
              // pulse trigger so RoleInventory always catches it
              onSubmitData={() => {
                setAprovedUser(true);
                setTimeout(() => setAprovedUser(false), 0);
              }}
              setIsModalOpen={setIsModalEditOpen}
              modalType={eFormType.Edit}
            >
              <div className="roleInvWrap">
                <RoleInventory
                  createOrUpdate={aprovedUser}
                  tempUser={selectedVal}
                  selectedCompanyId={selectedVal.companyId}
                  modalType={eFormType.Edit}
                  onSubmitData={() => {
                    getTable();
                    setIsModalEditOpen(false);
                  }}
                />
              </div>

              <div className="rolePermsWrap">
                <RoleGrantsGrid roleId={selectedVal.id} />
              </div>
            </EditModal>
          )}

          {/* Delete */}
          {isModalDeleteOpen && (
            <DeleteModal width="500px" handleClose={() => setIsModalDeleteOpen(false)} handleDelete={() => deleteDataSet(selectedVal?.id ?? 0)}>
              {i18n.t('generic.wantToDelete')} <strong className="text-danger"> {selectedVal?.id}</strong>.
            </DeleteModal>
          )}

          {/* New */}
          {isModalOpen && (
            <EditModal
              title={i18n.t('roles.addRoles')}
              width="80%"
              closeModal={() => setIsModalOpen(false)}
              onSubmitData={() => {
                setAprovedUser(true);
                setTimeout(() => setAprovedUser(false), 0);
              }}
              setIsModalOpen={setIsModalOpen}
              modalType={eFormType.New}
            >
              <div className="roleInvWrap">
                <RoleInventory
                  createOrUpdate={aprovedUser}
                  selectedCompanyId={selectedCompanyId}
                  modalType={eFormType.New}
                  onSubmitData={() => {
                    getTable();
                    setIsModalOpen(false);
                  }}
                />
              </div>
            </EditModal>
          )}
        </div>
      </div>
    </BasicCli>
  );
};

export default RolesDetailsGrid;
